import { AnalysisStyle } from '../types';
import { API_ENDPOINTS } from '../config/api';
import { supabase } from '../lib/supabase';

export interface UploadResult {
  imageUrl: string;
}

export interface AnalysisResponse {
  id: string;
  analysis: string;
  questions: string[];
  context: {
    topic: string;
    industry: string;
    contentType: string;
    tone: string;
  };
  imageUrl: string;
}

interface StoredAnalysis {
  id: string;
  user_id: string;
  image_url: string;
  analysis_style: string;
  questions: string[];
  created_at: string;
}

async function storeAnalysisResult(
  userId: string,
  imageUrl: string,
  style: AnalysisStyle,
  questions: string[]
): Promise<StoredAnalysis> {
  try {
    const { data, error } = await supabase
      .from('image_analyses')
      .insert([
        {
          user_id: userId,
          image_url: imageUrl,
          analysis_style: style,
          questions: questions
        }
      ])
      .select()
      .single();

    if (error) {
      console.error('Error storing analysis:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in storeAnalysisResult:', error);
    throw error;
  }
}

async function storeTextAnalysisResult(
  userId: string,
  content: string,
  style: AnalysisStyle,
  questions: string[],
  analysis: string,
  context: any
): Promise<any> {
  try {
    const { data, error } = await supabase
      .from('text_analyses')
      .insert([{
        user_id: userId,
        content,
        analysis,
        questions,
        analysis_style: style,
        context
      }])
      .select()
      .single();

    if (error) {
      console.error('Error storing text analysis:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in storeTextAnalysisResult:', error);
    throw error;
  }
}

export async function uploadImage(file: File, userId: string): Promise<UploadResult> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);

    const response = await fetch(API_ENDPOINTS.UPLOAD_IMAGE, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'x-user-id': session.user.id
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`API error: ${error.message || 'Unknown error'}`);
    }

    const result = await response.json();
    
    if (!result.imageUrl) {
      throw new Error('No image URL in response');
    }

    return result;
  } catch (error) {
    console.error('Error in uploadImage:', error);
    throw error;
  }
}

export async function analyzeImageWithGPT4(imageUrl: string, style: AnalysisStyle): Promise<AnalysisResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch(API_ENDPOINTS.ANALYZE_IMAGE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'x-user-id': session.user.id
      },
      body: JSON.stringify({
        imageUrl,
        style,
        userId: session.user.id
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`API error: ${error.message || 'Unknown error'}`);
    }

    const result = await response.json();
    console.log('Analysis result:', result);

    // Store the analysis in the database
    const storedAnalysis = await storeAnalysisResult(
      session.user.id,
      imageUrl,
      style,
      result.questions
    );
    console.log('Stored analysis:', storedAnalysis);

    // Return the combined result with the database ID
    return {
      id: storedAnalysis.id, // Add the database ID
      analysis: result.analysis,
      questions: result.questions,
      context: result.context,
      imageUrl: imageUrl
    };
  } catch (error) {
    console.error('Error in analyzeImageWithGPT4:', error);
    throw error;
  }
}

export async function analyzeTextWithGPT4(text: string, style: AnalysisStyle): Promise<AnalysisResponse> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }

    const response = await fetch('https://serv.adacadabra.ai/api/text-analysis', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'x-user-id': session.user.id
      },
      body: JSON.stringify({
        text,
        style,
        type: 'text'
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`API error: ${error.message || 'Unknown error'}`);
    }

    const result = await response.json();
    
    // Store the analysis in the text_analyses table
    const storedAnalysis = await storeTextAnalysisResult(
      session.user.id,
      text,
      style,
      result.questions,
      result.analysis,
      result.context
    );

    // Return the combined result with the database ID
    return {
      id: storedAnalysis.id,
      analysis: result.analysis,
      questions: result.questions,
      context: result.context,
      imageUrl: '' // no image url for text analysis
    };
  } catch (error) {
    console.error('Error in analyzeTextWithGPT4:', error);
    throw error;
  }
}